import type { FunctionComponent } from 'preact';
import { useEffect, useState } from 'preact/hooks';

import type { RepeaterFunctionInvoker, RepeaterRowRenderer, TemplateFunction } from 'Components/types';
import { createSelectedVehicleSelectionSelector } from 'Core/selectors/fitmentSearch/index';
import { useSelector } from 'Core/store';
import { logRuntimeError } from 'Diagnostics/diagnostics';
import { type SearchItemData, SearchItemDataCollection } from 'Models/index';
import type { SearchItemTemplateParams } from 'Models/searchItemData';
import productConfig from 'Models/uiConfig/productConfig';
import requestConfig from 'Models/uiConfig/requestConfig';
import { search } from 'Modules/serverApi/index';

type ItemTemplateParams = SearchItemTemplateParams<{
  isSelected: boolean;
  selectItem: () => void;
}>;

type Params = {
  items: RepeaterFunctionInvoker<ItemTemplateParams>;
  totalPrice: string;
  addItemsToCart: () => void;
};

type Props = {
  template: TemplateFunction<Params>;
  name: string;
  mode: string;
  count?: number;
  onItemsRendered?: () => void;
};

const { localItemId, formatPrice } = productConfig;
const { addItemsToCart } = requestConfig;

const BuyItemsTogether: FunctionComponent<Props> = ({ template, mode, count, onItemsRendered }) => {
  const vehicleSelection = useSelector(createSelectedVehicleSelectionSelector);

  const [selectedItemIds, setSelectedItemIds] = useState<string[]>([]);
  const [items, setItems] = useState<SearchItemDataCollection>();
  const [totalPrice, setTotalPrice] = useState<number>(0);

  useEffect(() => {
    if (!count) {
      return;
    }

    search({
      selection: [...vehicleSelection],
      pageSize: count,
      extra: { itemId: localItemId, mode },
    })
      .then((response) => {
        if (response.Items.length > 0) {
          const itemCollection = new SearchItemDataCollection(response.Items, 'boughtTogether');
          const ids = itemCollection.map((item) => item.entityId as string);

          setSelectedItemIds(ids);
          setItems(itemCollection);

          setTotalPrice(
            itemCollection
              .map((item) => ((item.price as number) || (item.get('compare_at_price') as number)) ?? 0)
              .reduce((total, price) => total + price, 0),
          );
        } else {
          setSelectedItemIds([]);
          setItems(undefined);
          setTotalPrice(0);
        }
      })
      .catch((err) => {
        logRuntimeError({ source: 'buy_items_together_request', error: err });
        console.error('BuyItemsTogether search failed', err);
      });
  }, [count, mode, vehicleSelection]);

  useEffect(() => {
    if (items?.length) {
      onItemsRendered?.();
    }
  }, [items, onItemsRendered]);

  if (!items?.length) {
    return;
  }

  const handleItemSelection = (itemId: string) => {
    const isSelected = selectedItemIds.includes(itemId);
    setSelectedItemIds(
      isSelected ? selectedItemIds.filter((id) => id !== itemId) : [...selectedItemIds, itemId],
    );

    const item = items.find((item) => item.entityId === itemId);
    const itemPrice = item?.price as number;
    const itemCompareAtPrice = item?.get('compare_at_price') as number;
    setTotalPrice(
      isSelected
        ? totalPrice - (itemPrice ?? itemCompareAtPrice)
        : totalPrice + (itemPrice ?? itemCompareAtPrice),
    );
  };

  const itemComponents: RepeaterRowRenderer<ItemTemplateParams, SearchItemData, 'item'> = ({
    template,
    item,
  }) => {
    const itemId = String(item.entityId);
    return template.call({
      ...item.getParams(vehicleSelection),
      isSelected: selectedItemIds.includes(itemId),
      selectItem: () => handleItemSelection(itemId),
    });
  };

  return template.call({
    items: items.repeaterComponents(itemComponents),
    totalPrice: formatPrice(totalPrice),
    addItemsToCart: () => addItemsToCart(selectedItemIds),
  });
};

export default BuyItemsTogether;
